/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { getVinDetails } from '@/api/ediagnostics';
import { usePrograms, useProgramSearch, useRecentVehicles } from '@/api/hooks';
import type { Program } from '@/api/models';
import { Icon, type IconName } from '@/components/Icon';
import i18n from '@/i18n/i18n';
import { Select } from '@/design-system';
import { programForVin } from '@/features/home/vinProgram';
import { cn } from '@/lib/cn';
import { ENTRY_SOURCE_PARAM } from '@/lib/entrySource';
import { lookupVehicleMakeModel } from '@/lib/programMatch';
import { orderedReleases, useDataSetField, type ReleaseOption } from '@/lib/releaseOptions';
import { searchString } from '@/lib/searchString';
import { useIsDealerUser, useIsInternalUser } from '@/lib/userRole';
import { vehicleContext, vehicleDisplay } from '@/lib/vehicleLabel';
import {
  DIAG_PROGRAM_HINT,
  VIN_HINT,
  VIN_MAX_LENGTH,
  VIN_PLACEHOLDER,
  canonicalVin,
  isValidVin,
  normalizeVin,
} from '@/lib/vin';
import { useUiStore, type VehicleIdentity } from '@/stores/uiStore';

type Mode = 'vin' | 'vehicle' | 'program';

/** Routes that require a vehicle context; the picker auto-expands here when none is loaded. */
const CONTEXT_ROUTES = ['/mecl', '/schematic-explorer'];

/** Remembers the last lookup method (VIN / Vehicle / Program) across sessions. */
const MODE_KEY = 'vp:mode';

/**
 * The field dropdowns render their list into a popper attached to <body>, so a pointerdown on an
 * option is outside the picker element even though the user is still inside the picker.
 */
const POPPER_SURFACE = '[data-radix-popper-content-wrapper]';

/** A field dropdown that currently has its list open (the dropdown owns Escape while it does). */
const OPEN_FIELD = '[data-state="open"]';

const uniq = (xs: (string | undefined)[]) => [...new Set(xs.filter(Boolean) as string[])];

/**
 * Rendered vehicle summary shared by the interactive picker toggle and the read-only badge.
 *
 * The vehicle description always leads and its identifiers always follow as chips, so switching
 * lookup method changes the values in the summary but never its shape.
 */
function VehicleInfoContent({
  vehicle,
  internal,
}: {
  vehicle: VehicleIdentity;
  internal: boolean;
}) {
  const { t } = useTranslation();
  const { primary, vinPrimary, vin, code, release, trim } = vehicleContext(vehicle, internal);
  return (
    <>
      <span className={cn('tb-name', vinPrimary && 'tb-vin-strong')}>{primary}</span>
      {(trim || vin || code || release) && (
        <span className="tb-sub">
          {trim && (
            <span className="tb-trim">
              <span className="tb-trim-k">{t('vehiclePicker.trim')}</span>
              <span className="tb-trim-v">{trim}</span>
            </span>
          )}
          {vin && <span className="tb-chip tb-chip-vin">{vin}</span>}
          {code && <span className="tb-chip">{code}</span>}
          {release && <span className="tb-chip tb-chip-rel">{release}</span>}
        </span>
      )}
    </>
  );
}

/**
 * Shared top-bar vehicle picker; resolves via query-param contract.
 *
 * `readOnly` renders a non-interactive vehicle summary (no dropdown) — used on the standalone 3D
 * viewer reached from MECL, where the vehicle context is fixed and should only be displayed.
 */
export function VehiclePicker({ readOnly = false }: { readOnly?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const vehicle = useUiStore((s) => s.vehicle);
  const setPickerOpen = useUiStore((s) => s.setPickerOpen);

  // Open/close intent scoped to route; switching tools reverts to that route's default.
  const [override, setOverride] = useState<{ path: string; open: boolean } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const internal = useIsInternalUser();
  const needsVehicle = CONTEXT_ROUTES.some((r) => pathname.startsWith(r));
  // A program/VIN already in the URL means the page is about to resolve it into a vehicle; don't
  // flash an empty picker in that gap (deep links / tool switches). Only auto-open when there's
  // genuinely nothing to resolve and the user must pick.
  // Accept the legacy uppercase `VIN` alias too (the picker panel and explorer URL parsing both
  // read `vin ?? VIN`), so a `?VIN=` deep link is treated the same and never flashes the picker.
  // A year/make/model names a vehicle just as squarely, and the page resolves that into a program
  // the same way, so it must not be met with a picker asking for what the link already said.
  const hasVehicleParam = Boolean(
    params.get('program') ||
    params.get('vin') ||
    params.get('VIN') ||
    (params.get('year') && params.get('make') && params.get('model')),
  );
  // Auto-expand on context routes without a vehicle; user can override via toggle.
  const autoOpen = needsVehicle && !vehicle && !hasVehicleParam;
  const manualOpen = override?.path === pathname ? override.open : null;
  const open = manualOpen ?? autoOpen;

  // Publish open state so pages can offset content; reset on unmount.
  useEffect(() => {
    setPickerOpen(open);
    return () => setPickerOpen(false);
  }, [open, setPickerOpen]);

  // Auto-close on an outside click or Escape, but only once a vehicle is selected — while the picker
  // is forced open because none is chosen yet, closing it would just trap the user. (The read-only
  // picker never opens, so `open` already gates this out.)
  useEffect(() => {
    if (!open || !vehicle) return;
    const close = () => setOverride({ path: pathname, open: false });
    // Picking a value from a field dropdown must not read as leaving the picker, or changing the
    // model year of the loaded vehicle would collapse the panel on the first click.
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target;
      const wrap = wrapRef.current;
      if (!wrap || wrap.contains(target as Node)) return;
      // A synthetic event can target the document, which has no `closest`.
      if (target instanceof Element && target.closest(POPPER_SURFACE)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const wrap = wrapRef.current;
      if (e.key !== 'Escape' || !wrap) return;
      // An open field dropdown takes Escape first; a second press then closes the picker.
      if (wrap.querySelector(OPEN_FIELD)) return;
      close();
      toggleRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, vehicle, pathname]);

  const go = (params: Record<string, string | undefined>) => {
    const base = pathname.startsWith('/mecl') ? '/mecl' : '/schematic-explorer';
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v) search.set(k, v);
    }
    setOverride(null);
    navigate(`${base}${searchString(search)}`);
  };

  // Read-only badge: non-interactive vehicle context (e.g. the 3D viewer launched from MECL).
  if (readOnly) {
    if (!vehicle) return null;
    return (
      <div className="vp vp--readonly" data-tour="vehicle-picker">
        <div
          className="vp-static"
          aria-label={t('vehiclePicker.currentVehicle', 'Current vehicle')}
        >
          <span className="vp-static-ic">
            <Icon name="vehicle" size={16} />
          </span>
          <span className="vp-veh-info">
            <VehicleInfoContent vehicle={vehicle} internal={internal} />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="vp" data-tour="vehicle-picker" ref={wrapRef}>
      <button
        type="button"
        ref={toggleRef}
        className={cn('vp-toggle', open && 'is-open')}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOverride({ path: pathname, open: !open })}
      >
        <span className="vp-toggle-ic">
          <Icon name="vehicle" size={16} />
        </span>
        <span className="vp-veh-info">
          {vehicle ? (
            <VehicleInfoContent vehicle={vehicle} internal={internal} />
          ) : (
            <>
              <span className="tb-name">{t('vehiclePicker.selectVehicle')}</span>
              <span className="vp-toggle-hint">{t('vehiclePicker.toggleHint')}</span>
            </>
          )}
        </span>
        <Icon name="chevd" size={16} className="vp-caret" />
      </button>

      {open && <VehiclePickerPanel onResolve={go} />}
    </div>
  );
}

/** Expanded picker: method rail + fields + recent quick-picks. */
function VehiclePickerPanel({
  onResolve,
}: {
  onResolve: (params: Record<string, string | undefined>) => void;
}) {
  const { t } = useTranslation();
  const { data: vehiclePrograms = [], isLoading: vehiclesLoading } = usePrograms();
  const { data: searchPrograms = [], isLoading: programsLoading } = useProgramSearch();
  const { data: recentVehicles = [] } = useRecentVehicles();
  const internal = useIsInternalUser();
  // Dealers don't get the internal-only program lookup (its catalog 403s for them).
  const isDealer = useIsDealerUser();

  // Seed fields from URL params; panel remounts on each open so mount-time read is sufficient.
  const [sp] = useSearchParams();
  const cur = {
    vin: sp.get('vin') ?? sp.get('VIN') ?? '',
    system: sp.get('system') ?? '',
    year: sp.get('year') ?? '',
    make: sp.get('make') ?? '',
    model: sp.get('model') ?? '',
    release: sp.get('release') ?? '',
    programCode: sp.get('programCode') ?? '',
  };

  // Deep links / tool switches often carry only ?program=<id>; the code/year/make/model/release
  // live on the page's resolved program, not the URL. Resolve the id against the loaded catalogs so
  // reopening the picker lands on the matching tab with its fields filled. Prefer the vehicle
  // catalog (carries make/model) over the program-search catalog.
  const programId = Number(sp.get('program')) || null;
  const programMatch = useMemo(
    () =>
      programId
        ? [...vehiclePrograms, ...searchPrograms].find((p) => p.id === programId)
        : undefined,
    [vehiclePrograms, searchPrograms, programId],
  );
  // Prefer the explicit URL value, then the resolved program (the vehicle catalog carries make/model).
  const seedProgramCode = cur.programCode || programMatch?.programCode || '';
  const seedYear = cur.year || programMatch?.modelYear || '';
  const seedRelease = cur.release || programMatch?.engineeringRelease || '';
  const seedMake = cur.make || programMatch?.make || '';
  const seedModel = cur.model || programMatch?.model || '';

  // Method that produced the loaded vehicle, so reopening the picker starts on the matching tab
  // (VIN / vehicle / program) rather than always VIN. A URL make means an explicit vehicle lookup;
  // a program (code in the URL or resolved from the id) with no make is a program lookup. VIN wins
  // because a VIN launch also carries make/year.
  const detectMode = (): Mode | null => {
    if (cur.vin) return 'vin';
    if (cur.make) return 'vehicle';
    if (seedProgramCode) return 'program';
    return null;
  };
  // Default to the current vehicle's method; with none loaded, restore the last method used.
  const [mode, setMode] = useState<Mode>(() => {
    /* v8 ignore next -- defensive: localStorage is always defined in the browser/jsdom runtime. */
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(MODE_KEY) : null;
    const remembered =
      saved === 'vin' || saved === 'vehicle' || saved === 'program' ? saved : 'vin';
    const initial = detectMode() ?? remembered;
    // Dealers have no program lookup; fall back to VIN if that was the resolved method.
    return isDealer && initial === 'program' ? 'vin' : initial;
  });
  // Set once the user explicitly picks a lookup method, so the async re-seed below
  // never yanks them off a tab they chose while the catalogs were still loading.
  const userChoseModeRef = useRef(false);
  const chooseMode = (m: Mode) => {
    userChoseModeRef.current = true;
    setMode(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* storage unavailable (private mode) - non-fatal */
    }
  };

  const [vin, setVin] = useState(cur.vin);
  const [diagSystem, setDiagSystem] = useState(cur.system);
  const [vinLoading, setVinLoading] = useState(false);
  const [vinError, setVinError] = useState('');

  const [vYear, setVYear] = useState(seedYear);
  const [vMake, setVMake] = useState(seedMake);
  const [vModel, setVModel] = useState(seedModel);
  const [vRelease, setVRelease] = useState(seedRelease);

  const [pYear, setPYear] = useState(seedYear);
  const [pProgram, setPProgram] = useState(seedProgramCode);
  const [pRelease, setPRelease] = useState(seedRelease);

  // usePrograms()/useProgramSearch() load asynchronously, so opening the picker on a
  // ?program=<id> deep link before the catalogs resolve seeds the Program tab from an
  // undefined programMatch (fields blank, wrong tab) and the mount-time useState never
  // re-runs. Re-seed once the match resolves - but only when the URL carried nothing but
  // the program id (a program lookup) and the user hasn't already picked a method, so an
  // in-progress entry is never clobbered. Dealers have no program lookup, so skip them.
  const needsAsyncProgramSeed =
    Boolean(programId) && !isDealer && !cur.vin && !cur.make && !cur.programCode;
  useEffect(() => {
    if (!needsAsyncProgramSeed || !programMatch || userChoseModeRef.current) return;
    setMode('program');
    setPProgram(seedProgramCode);
    setPYear(seedYear);
    setPRelease(seedRelease);
  }, [needsAsyncProgramSeed, programMatch, seedProgramCode, seedYear, seedRelease]);

  const vYears = useMemo(
    () =>
      uniq(vehiclePrograms.map((p) => p.modelYear))
        .sort()
        .reverse(),
    [vehiclePrograms],
  );
  const vMakes = useMemo(
    () => uniq(vehiclePrograms.filter((p) => p.modelYear === vYear).map((p) => p.make)),
    [vehiclePrograms, vYear],
  );
  const vModels = useMemo(
    () =>
      uniq(
        vehiclePrograms
          .filter((p) => p.modelYear === vYear && p.make === vMake)
          .map((p) => p.model),
      ),
    [vehiclePrograms, vYear, vMake],
  );
  const dataSet = useDataSetField(
    vehiclePrograms,
    { modelYear: vYear, make: vMake, model: vModel },
    vRelease,
  );
  // Internal users choose a release by its code; dealers choose a numbered data set, as in the
  // data-set dialog, so neither the label nor the prompt leaks a code to them.
  const dataSetLabel = internal ? t('vehiclePicker.launchRelease') : t('vehiclePicker.dataSet');
  const dataSetPlaceholder = internal
    ? t(vModel ? 'vehiclePicker.selectRelease' : 'vehiclePicker.modelFirst')
    : t('explorer.dataSet.title');

  const pYears = useMemo(
    () =>
      uniq(searchPrograms.map((p) => p.modelYear))
        .sort()
        .reverse(),
    [searchPrograms],
  );
  const pPrograms = useMemo(
    () =>
      uniq(searchPrograms.filter((p) => p.modelYear === pYear).map((p) => p.programCode)).sort(),
    [searchPrograms, pYear],
  );
  const pReleases = useMemo(
    () =>
      uniq(
        searchPrograms
          .filter((p) => p.modelYear === pYear && p.programCode === pProgram)
          .map((p) => p.engineeringRelease),
      ).sort(),
    [searchPrograms, pYear, pProgram],
  );

  const recent = useMemo(() => {
    const seen = new Set<string>();
    const out: {
      vin: string;
      system?: string;
      year?: string;
      make?: string;
      model?: string;
      programId?: number;
      programCode?: string;
      release?: string;
    }[] = [];
    for (const v of recentVehicles) {
      if (!v.vin || seen.has(v.vin)) continue;
      seen.add(v.vin);
      out.push({
        vin: v.vin,
        system: v.system,
        year: v.year,
        make: v.make,
        model: v.rawModel,
        programId: v.programId,
        programCode: v.program,
        release: v.release,
      });
      if (out.length >= 4) break;
    }
    return out;
  }, [recentVehicles]);

  // Reopen a recent vehicle: instant when its program id is known, else resolve via AVP.
  const openRecent = (r: (typeof recent)[number]) => {
    setVin(r.vin);
    setDiagSystem(r.system ?? '');
    if (r.programId) {
      onResolve({
        [ENTRY_SOURCE_PARAM]: 'vin',
        program: String(r.programId),
        programCode: r.programCode || undefined,
        vin: r.vin,
        system: r.system || undefined,
        year: r.year || undefined,
        make: r.make || undefined,
        model: r.model || undefined,
        release: r.release || undefined,
      });
      return;
    }
    resolveVin(r.vin, r.system ?? '');
  };

  const resolveVin = async (vinValue: string, system: string) => {
    const normalized = normalizeVin(vinValue);
    if (!isValidVin(normalized)) {
      setVinError(i18n.t('vehiclePicker.vinErrorInvalid'));
      return;
    }
    setVinLoading(true);
    setVinError('');
    const catalog = [...searchPrograms, ...vehiclePrograms];
    // An unanswered lookup and an unserviceable vehicle both leave us without a program, but only
    // the first is worth retrying, so they get different words here as they do on the home card.
    let lookupFailed = false;
    const details = await getVinDetails(normalized, system).catch(() => {
      lookupFailed = true;
      return null;
    });
    const program = programForVin(catalog, details);
    setVinLoading(false);
    if (!program?.id) {
      setVinError(
        i18n.t(
          lookupFailed || details?.programLookupFailed
            ? 'vehiclePicker.vinErrorLookupFailed'
            : 'vehiclePicker.vinErrorNoProgram',
        ),
      );
      return;
    }
    // AVP is authoritative for make/model; fall back to the catalog when the code+year is unambiguous.
    /* v8 ignore next -- unreachable: resolution requires details.modelYear, so it is always set here. */
    const year = details?.modelYear ?? program.modelYear;
    const catalogMm = lookupVehicleMakeModel(catalog, program.programCode, year);
    onResolve({
      [ENTRY_SOURCE_PARAM]: 'vin',
      program: String(program.id),
      programCode: program.programCode,
      vin: canonicalVin(normalized, details?.vin),
      system: system || undefined,
      year,
      make: details?.make ?? program.make ?? catalogMm.make,
      model: details?.model ?? program.model ?? catalogMm.model,
      modelDesignator: details?.modelDesignator,
    });
  };

  // Only invoked once the cascade (data set included) is complete, so the release is always set.
  // The row is the one the field's options kept for that code, not the first catalog hit.
  const resolveVehicle = () => {
    const program = orderedReleases(
      vehiclePrograms.filter(
        (p: Program) => p.modelYear === vYear && p.make === vMake && p.model === vModel,
      ),
    ).find((p) => p.engineeringRelease === dataSet.value);
    /* v8 ignore next -- defensive: the release comes from the field's own options, so a program always matches. */
    if (program?.id) {
      onResolve({
        [ENTRY_SOURCE_PARAM]: 'vehicle-search',
        program: String(program.id),
        programCode: program.programCode,
        year: vYear,
        make: vMake,
        model: vModel,
        release: dataSet.value,
      });
    }
  };

  const pMatch = useMemo(
    () =>
      searchPrograms.find(
        (p: Program) =>
          p.modelYear === pYear && p.programCode === pProgram && p.engineeringRelease === pRelease,
      ) ?? null,
    [searchPrograms, pYear, pProgram, pRelease],
  );

  const resolveProgram = () => {
    /* v8 ignore next -- defensive: the only call site is gated on a truthy pMatch. */
    if (!pMatch?.id) return;
    onResolve({
      [ENTRY_SOURCE_PARAM]: 'program-search',
      program: String(pMatch.id),
      /* v8 ignore next -- defensive: pMatch matches on programCode === pProgram, so it is always set. */
      programCode: pMatch.programCode || pProgram,
      year: pYear,
      release: pRelease,
    });
  };

  const vehicleReady = Boolean(vYear && vMake && vModel && dataSet.value);

  const methods: { id: Mode; label: string; icon: IconName; hint: string }[] = [
    { id: 'vin', label: 'VIN', icon: 'scan', hint: t('vehiclePicker.methods.vin.hint') },
    {
      id: 'vehicle',
      label: t('vehiclePicker.methods.vehicle.label'),
      icon: 'vehicle',
      hint: t('vehiclePicker.methods.vehicle.hint'),
    },
    // Program lookup is internal-only; dealers can't read its catalog.
    ...(isDealer
      ? []
      : [
          {
            id: 'program' as const,
            label: t('vehiclePicker.methods.program.label'),
            icon: 'list' as IconName,
            hint: t('vehiclePicker.methods.program.hint'),
          },
        ]),
  ];

  return (
    <>
      <div className="vp-drop" role="dialog" aria-label={t('vehiclePicker.chooseVehicle')}>
        <nav className="vp-methods" aria-label={t('vehiclePicker.lookupMethod')}>
          {methods.map((m) => (
            <button
              key={m.id}
              type="button"
              className={cn('vp-method', mode === m.id && 'on')}
              aria-pressed={mode === m.id}
              onClick={() => chooseMode(m.id)}
            >
              <Icon name={m.icon} size={18} />
              <span className="vp-method-text">
                <span className="vp-method-label">{m.label}</span>
                <span className="vp-method-hint">{m.hint}</span>
              </span>
            </button>
          ))}
        </nav>

        <div className="vp-pane">
          {mode === 'vin' && (
            <div className="vp-fields">
              <PickerField label={t('vehiclePicker.diagnosticProgram')} hint={t(DIAG_PROGRAM_HINT)}>
                <div
                  className="vp-seg"
                  role="group"
                  aria-label={t('vehiclePicker.diagnosticProgram')}
                >
                  {['OnStar', 'SAVI'].map((o) => (
                    <button
                      key={o}
                      type="button"
                      aria-pressed={diagSystem === o}
                      className={cn('vp-seg-btn', diagSystem === o && 'on')}
                      onClick={() => setDiagSystem(diagSystem === o ? '' : o)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </PickerField>

              <PickerField label="VIN" hint={t(VIN_HINT)}>
                <div className="vp-vin-row">
                  <div className="vp-vin-input">
                    <Icon name="scan" size={17} />
                    <input
                      placeholder={t(VIN_PLACEHOLDER)}
                      value={vin}
                      maxLength={VIN_MAX_LENGTH}
                      onChange={(e) => {
                        setVin(e.target.value.toUpperCase());
                        setVinError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && isValidVin(vin) && !vinLoading)
                          resolveVin(vin, diagSystem);
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-pri vp-go"
                    onClick={() => resolveVin(vin, diagSystem)}
                    disabled={!isValidVin(vin) || vinLoading}
                  >
                    {vinLoading ? t('vehiclePicker.resolving') : t('vehiclePicker.open')}{' '}
                    <Icon name="arrow" size={15} />
                  </button>
                </div>
              </PickerField>

              {vinError && (
                <span className="vp-error" role="alert">
                  {vinError}
                </span>
              )}

              {recent.length > 0 && (
                <div className="vp-recent">
                  <span className="vp-recent-label">{t('vehiclePicker.recentVehicles')}</span>
                  <div className="vp-recent-list">
                    {recent.map((r) => {
                      const active = Boolean(cur.vin) && r.vin === cur.vin;
                      const { primary, code, release } = vehicleDisplay(
                        {
                          year: r.year,
                          make: r.make,
                          model: r.model,
                          programCode: r.programCode,
                          release: r.release,
                        },
                        internal,
                      );
                      return (
                        <button
                          key={r.vin}
                          type="button"
                          className={cn('vp-recent-row', active && 'is-active')}
                          aria-current={active ? 'true' : undefined}
                          disabled={vinLoading}
                          onClick={() => openRecent(r)}
                        >
                          <span className="vp-recent-ic">
                            <Icon name={active ? 'vehicle' : 'history'} size={15} />
                          </span>
                          <span className="vp-recent-main">
                            <span className="vp-recent-vin">{r.vin}</span>
                            {(primary || code || release) && (
                              <span className="vp-recent-desc">
                                {primary && <span className="vp-recent-veh">{primary}</span>}
                                {code && <span className="vp-recent-code">{code}</span>}
                                {release && <span className="vp-recent-code">{release}</span>}
                              </span>
                            )}
                          </span>
                          {active && (
                            <span className="vp-recent-badge">{t('vehiclePicker.current')}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {mode === 'vehicle' && (
            <div className="vp-fields">
              <div className="vp-grid">
                <PickerSelect
                  label={t('vehiclePicker.modelYear')}
                  value={vYear}
                  onChange={(v) => {
                    setVYear(v);
                    setVMake('');
                    setVModel('');
                    setVRelease('');
                  }}
                  options={vYears}
                  placeholder={
                    vehiclesLoading ? t('vehiclePicker.loading') : t('vehiclePicker.selectYear')
                  }
                  disabled={vehiclesLoading}
                />
                <PickerSelect
                  label={t('vehiclePicker.make')}
                  value={vMake}
                  onChange={(v) => {
                    setVMake(v);
                    setVModel('');
                    setVRelease('');
                  }}
                  options={vMakes}
                  placeholder={vYear ? t('vehiclePicker.selectMake') : t('vehiclePicker.yearFirst')}
                  disabled={!vYear}
                />
                <PickerSelect
                  label={t('vehiclePicker.model')}
                  value={vModel}
                  onChange={(v) => {
                    setVModel(v);
                    setVRelease('');
                  }}
                  options={vModels}
                  placeholder={
                    vMake ? t('vehiclePicker.selectModel') : t('vehiclePicker.makeFirst')
                  }
                  disabled={!vMake}
                />
                {dataSet.visible && (
                  <PickerSelect
                    label={dataSetLabel}
                    value={dataSet.value}
                    onChange={setVRelease}
                    options={dataSet.options}
                    placeholder={dataSetPlaceholder}
                    disabled={!vModel}
                  />
                )}
              </div>

              <div className="vp-vehicle-actions">
                <button
                  type="button"
                  className="btn btn-pri vp-go"
                  onClick={resolveVehicle}
                  disabled={!vehicleReady}
                >
                  {t('vehiclePicker.open')} <Icon name="arrow" size={15} />
                </button>
              </div>
            </div>
          )}

          {mode === 'program' && (
            <div className="vp-fields">
              <div className="vp-grid">
                <PickerSelect
                  label={t('vehiclePicker.modelYear')}
                  value={pYear}
                  onChange={(v) => {
                    setPYear(v);
                    setPProgram('');
                    setPRelease('');
                  }}
                  options={pYears}
                  placeholder={
                    programsLoading ? t('vehiclePicker.loading') : t('vehiclePicker.selectYear')
                  }
                  disabled={programsLoading}
                />
                <PickerSelect
                  label={t('vehiclePicker.program')}
                  value={pProgram}
                  onChange={(v) => {
                    setPProgram(v);
                    setPRelease('');
                  }}
                  options={pPrograms}
                  placeholder={
                    pYear ? t('vehiclePicker.selectProgram') : t('vehiclePicker.yearFirst')
                  }
                  disabled={!pYear}
                />
                <PickerSelect
                  label={t('vehiclePicker.engineeringRelease')}
                  value={pRelease}
                  onChange={setPRelease}
                  options={pReleases}
                  placeholder={
                    pProgram ? t('vehiclePicker.selectRelease') : t('vehiclePicker.programFirst')
                  }
                  disabled={!pProgram}
                />
              </div>

              <div className="vp-vehicle-actions">
                <button
                  type="button"
                  className="btn btn-pri vp-go"
                  onClick={resolveProgram}
                  disabled={!pMatch}
                >
                  {t('vehiclePicker.open')} <Icon name="arrow" size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function PickerField({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn('vp-field', className)}>
      <span className="vp-fl">
        {label}
        {hint && (
          <span
            className="vp-hint"
            role="button"
            tabIndex={0}
            aria-label={hint}
            data-tip={hint}
            onClick={(e) => e.preventDefault()}
          >
            <Icon name="info" size={13} />
          </span>
        )}
      </span>
      {children}
    </label>
  );
}

function PickerSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Plain codes, or `{ value, label }` where the label adds context the value cannot carry. */
  options: (string | ReleaseOption)[];
  placeholder: string;
  disabled?: boolean;
}) {
  return (
    <PickerField label={label}>
      <Select
        // Remount on empty↔filled so the underlying Radix select stays consistently controlled
        // (the kit maps a falsy value to `undefined`, which otherwise trips its controlled warning).
        key={value ? 'filled' : 'empty'}
        value={value}
        onValueChange={onChange}
        options={options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o))}
        placeholder={placeholder}
        disabled={disabled}
        ariaLabel={label}
        // The kit draws the GMDS field (control fill, strong outline, field radius); the picker
        // only pins the height so the selects line up with the VIN field beside them.
        className="h-10"
      />
    </PickerField>
  );
}
